/**
 * Rozpoznawanie banku na podstawie numeru IBAN oraz formatowanie numeru.
 *
 * Polski IBAN ma 28 znaków: PL + 2 cyfry kontrolne + 8 cyfr numeru rozliczeniowego + 16 cyfr numeru klienta.
 * Pierwsze 4 cyfry numeru rozliczeniowego (pozycje 5-8 całego IBAN) identyfikują bank.
 *
 * Lista jest statyczna i nie wymaga zapytania do serwera — kod banku jest zaszyty w numerze.
 * Uwaga: przy fuzjach banków kody bywają przenoszone; listę trzeba okresowo weryfikować
 * z wykazem NBP (https://ewib.nbp.pl).
 */

export const POLISH_BANK_CODES: Record<string, string> = {
  '1010': 'Narodowy Bank Polski',
  '1020': 'PKO Bank Polski',
  '1030': 'Citi Handlowy',
  '1050': 'ING Bank Śląski',
  '1060': 'Bank BPH',
  '1090': 'Santander Bank Polska',
  '1130': 'Bank Gospodarstwa Krajowego',
  '1140': 'mBank',
  '1160': 'Bank Millennium',
  '1240': 'Bank Pekao',
  '1320': 'Bank Pocztowy',
  '1470': 'Euro Bank',
  '1540': 'Bank Ochrony Środowiska',
  '1580': 'Mercedes-Benz Bank Polska',
  '1610': 'SGB-Bank',
  '1670': 'RBS Bank Polska',
  '1680': 'Plus Bank',
  '1750': 'BNP Paribas Bank Polska',
  '1840': 'Societe Generale',
  '1870': 'Nest Bank',
  '1880': 'Deutsche Bank Polska',
  '1930': 'Bank Polskiej Spółdzielczości',
  '1940': 'Credit Agricole Bank Polska',
  '1950': 'Idea Bank',
  '2030': 'BNP Paribas Bank Polska',
  '2120': 'Santander Consumer Bank',
  '2130': 'Volkswagen Bank',
  '2160': 'Toyota Bank Polska',
  '2190': 'DNB Bank Polska',
  '2480': 'Getin Noble Bank',
  '2490': 'Alior Bank',
  '2530': 'Nest Bank',
  '2710': 'Alior Bank',
  '2770': 'Volkswagen Financial Services',
  '3010': 'Aion Bank',
};

/** Usuwa spacje i normalizuje do wielkich liter. */
export function normalizeIban(rawIban: string): string {
  return (rawIban || '').replace(/\s+/g, '').toUpperCase();
}

/**
 * Zwraca nazwę banku odczytaną z numeru IBAN albo null, jeśli nie da się jej ustalić.
 * Działa też dla numeru bez prefiksu "PL" i dla numeru jeszcze niekompletnego —
 * wystarczy, że wpisane są cyfry na pozycjach kodu banku.
 */
export function getBankNameFromIban(rawIban: string): string | null {
  let iban = normalizeIban(rawIban);
  if (!iban) return null;

  // Numer bez prefiksu kraju traktujemy jak polski — spójnie z validateIBAN().
  if (!/^[A-Z]{2}/.test(iban)) {
    iban = 'PL' + iban;
  }
  if (!iban.startsWith('PL')) return null;

  const bankCode = iban.slice(4, 8);
  if (!/^\d{4}$/.test(bankCode)) return null;

  if (POLISH_BANK_CODES[bankCode]) return POLISH_BANK_CODES[bankCode];

  // Banki spółdzielcze mają kody z zakresów 8xxx i 9xxx — nie wymieniamy ich pojedynczo.
  if (/^[89]/.test(bankCode)) return 'Bank spółdzielczy';

  return null;
}

/** Formatuje numer do postaci czytelnej: PL61 1090 1014 0000 0712 1981 2874 */
export function formatIban(rawIban: string): string {
  const iban = normalizeIban(rawIban);
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

/** Przykładowy poprawny numer — używany jako podpowiedź w polu formularza. */
export const IBAN_PLACEHOLDER = 'PL61 1090 1014 0000 0712 1981 2874';
